import type { ProjectDetail } from "@esi/shared";
import { FilePlus2, FolderOpen, Upload, UserPlus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Link, newPagePath, projectHomePath, projectSettingsPath } from "../../router.tsx";
import { canChange } from "../capabilities.ts";

/**
 * Getting started (design D7): in place of the sections while the project has no page, no
 * source and no conversation the viewer can read. It offers only what the viewer may do, as
 * small outline actions, and none in a frozen project. Asking is the header's field, right
 * above: it shows exactly where a conversation may start (never frozen), so no step repeats it.
 */
export function GettingStarted({ project }: { project: ProjectDetail }) {
  const { t } = useTranslation();
  const frozen = project.frozenAt !== null;
  const actions = [
    canChange(project, "file.upload")
      ? {
          key: "sources",
          icon: Upload,
          href: projectHomePath(project.id, "files"),
          label: t("projectOverview.start.addSources"),
        }
      : null,
    canChange(project, "wiki.draft")
      ? {
          key: "page",
          icon: FilePlus2,
          href: newPagePath(project.id),
          label: t("projectOverview.start.newPage"),
        }
      : null,
    canChange(project, "members.manage")
      ? {
          key: "invite",
          icon: UserPlus,
          href: projectSettingsPath(project.id, "members"),
          label: t("projectOverview.start.invite"),
        }
      : null,
  ].filter((a) => a !== null);

  return (
    <section data-home-section="getting-started" className="py-2">
      <EmptyState
        icon={FolderOpen}
        title={t("projectOverview.start.title")}
        action={
          actions.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {actions.map((action) => {
                const Icon = action.icon;
                return (
                  <Button key={action.key} asChild size="sm" variant="outline">
                    <Link href={action.href}>
                      <Icon aria-hidden="true" strokeWidth={1.75} />
                      {action.label}
                    </Link>
                  </Button>
                );
              })}
            </div>
          ) : undefined
        }
      >
        {frozen
          ? t("projectOverview.start.leadFrozen")
          : actions.length > 0
            ? t("projectOverview.start.lead")
            : t("projectOverview.start.leadReader")}
      </EmptyState>
    </section>
  );
}
